import React, { useCallback, useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import priyankaImage from '../../assets/PriyankaParticipation.jpeg';
import rashmiImage from '../../assets/Ankita.jpeg';
import atulImage from '../../assets/atul.jpeg';
import shonaImage from '../../assets/shona.jpeg';
import rajatImage from '../../assets/RAJAT DHARR.jpeg';
import sujataImage from '../../assets/sujata.jpeg';
import anuritaajiImage from '../../assets/anuritaaji.jpeg';

const participations = [
  {
    image: anuritaajiImage,
    alt: 'Anurita Aji',
    name: 'Anurita Aji',
    description:
      "Accomplished artist Anuritaaji's participation in DRISHTIKON HIMMATWAALI MISSION",
  },
  {
    image: sujataImage,
    alt: 'Sujata',
    name: 'Sujata',
    description:
      "Devoted Homemaker Sujata  participation in DRISHTIKON HIMMATWAALI MISSION",
  },
  {
    image: rajatImage,
    alt: 'Rajat Dharr',
    name: 'Rajat Dharr',
    description:
      "Accomplished artist Rajat Dharr's participation in DRISHTIKON HIMMATWAALI MISSION",
  },
  {
    image: shonaImage,
    alt: 'Shona Chanterjjee',
    name: 'Shona Chanterjjee',
    description:
      'Gifted Performing Artist Shona Chanterjjee contributes her precious talent to DRISHTIKON HIMMATWAALI MISSION',
  },
  {
    image: priyankaImage,
    alt: 'Priyanka Timmins participating in the project',
    name: 'Priyanka Timmins',
    description:
      'Gifted Performing Artist Priyanka Timmins contributes her precious talent to DRISHTIKON HIMMATWAALI MISSION',
  },
  {
    image: rashmiImage,
    alt: 'Rashmi Bhowmick participating in the project',
    name: 'Rashmi Bhowmick',
    description:
      'Gifted Voice Artist Rashmi Bhowmick graciously lends her exceptional talent to DRISHTIKON HIMMATWAALI MISSION',
  },
  {
    image: atulImage,
    alt: 'Atul Sarin participating in the project',
    name: 'Atul Sarin',
    description:
      'Senior Advertising Professional Atul Sarin contributes his precious guidance towards creating awareness for social issues in support of DRISHTIKON HIMMATWAALI MISSION',
  },
];

const DESKTOP_VISIBLE = 3;

function useVisibleSlideCount() {
  const [count, setCount] = useState(1);

  useEffect(() => {
    const media = window.matchMedia('(min-width: 1024px)');
    const sync = () => setCount(media.matches ? DESKTOP_VISIBLE : 1);
    sync();
    media.addEventListener('change', sync);
    return () => media.removeEventListener('change', sync);
  }, []);

  return count;
}

const ParticipationCard = ({ entry }) => (
  <article className="flex h-full flex-col overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-md">
    <div className="flex min-h-[280px] items-center justify-center bg-[#f7f5f2] p-4 sm:min-h-[320px] lg:min-h-[360px]">
      <img
        src={entry.image}
        alt={entry.alt}
        className="max-h-[min(520px,72vh)] w-full object-contain object-center"
        loading="lazy"
      />
    </div>
    <div className="flex flex-1 flex-col gap-2 p-5 md:p-6">
      <h3 className="text-lg font-semibold text-primary md:text-xl">{entry.name}</h3>
      <p className="text-sm leading-relaxed text-gray-700 md:text-base">
        {entry.description}
      </p>
    </div>
  </article>
);

const HomeParticipation = () => {
  const visibleCount = useVisibleSlideCount();
  const total = participations.length;
  const maxIndex = Math.max(0, total - visibleCount);
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    setActiveIndex((prev) => Math.min(prev, maxIndex));
  }, [maxIndex]);

  const goTo = useCallback(
    (index) => {
      setActiveIndex(Math.max(0, Math.min(index, maxIndex)));
    },
    [maxIndex],
  );

  const goPrev = () => goTo(activeIndex - 1);
  const goNext = () => goTo(activeIndex + 1);

  const useCarousel = total > visibleCount;
  const trackWidthPercent = (total / visibleCount) * 100;
  const slideShareOfTrack = 100 / total;

  const centeredSlotClass =
    total === 1
      ? 'w-full max-w-md'
      : total === 2
        ? 'w-full max-w-md lg:max-w-[calc((100%-2.5rem)/2)] lg:flex-[1_1_0]'
        : 'w-full max-w-md lg:max-w-[calc((100%-5rem)/3)] lg:flex-[1_1_0]';

  return (
    <section className="bg-bg-light px-6 py-14 md:px-12">
      <div className="mx-auto max-w-6xl">
        <h2 className="mb-10 text-center text-3xl font-bold uppercase text-accent md:mb-12 md:text-4xl">
          Participation
        </h2>

        {useCarousel ? (
          <div className="overflow-hidden">
            <div
              className="flex transition-transform duration-500 ease-out"
              style={{
                width: `${trackWidthPercent}%`,
                transform: `translateX(-${activeIndex * slideShareOfTrack}%)`,
              }}
            >
              {participations.map((entry) => (
                <div
                  key={entry.alt}
                  className="box-border flex-shrink-0 px-2 sm:px-3"
                  style={{ width: `${slideShareOfTrack}%` }}
                >
                  <ParticipationCard entry={entry} />
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="flex flex-wrap items-stretch justify-center gap-6 lg:gap-10">
            {participations.map((entry) => (
              <div key={entry.alt} className={centeredSlotClass}>
                <ParticipationCard entry={entry} />
              </div>
            ))}
          </div>
        )}

        {useCarousel && (
          <div className="mt-8 flex flex-wrap items-center justify-center gap-4 sm:gap-6">
            <button
              type="button"
              onClick={goPrev}
              disabled={activeIndex === 0}
              className="rounded-full border border-gray-200 bg-white p-2.5 text-primary shadow-sm transition hover:bg-white/80 disabled:cursor-not-allowed disabled:opacity-40"
              aria-label="Previous participation"
            >
              <ChevronLeft className="h-6 w-6" aria-hidden />
            </button>

            <div
              className="flex flex-wrap justify-center gap-2"
              role="tablist"
              aria-label="Participation slides"
            >
              {Array.from({ length: maxIndex + 1 }, (_, pageIndex) => (
                <button
                  key={pageIndex}
                  type="button"
                  role="tab"
                  aria-selected={pageIndex === activeIndex}
                  aria-label={`Go to slide group ${pageIndex + 1}`}
                  onClick={() => goTo(pageIndex)}
                  className={`h-2.5 rounded-full transition-all ${
                    pageIndex === activeIndex
                      ? 'w-8 bg-accent'
                      : 'w-2.5 bg-gray-300 hover:bg-gray-400'
                  }`}
                />
              ))}
            </div>

            <button
              type="button"
              onClick={goNext}
              disabled={activeIndex >= maxIndex}
              className="rounded-full border border-gray-200 bg-white p-2.5 text-primary shadow-sm transition hover:bg-white/80 disabled:cursor-not-allowed disabled:opacity-40"
              aria-label="Next participation"
            >
              <ChevronRight className="h-6 w-6" aria-hidden />
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

export default HomeParticipation;
